import * as React from "react"
import { Search, Bell, CreditCard, ChevronRight, Plus, Coins, ArrowLeft } from "lucide-react"
import { cn } from "../../lib/utils"
import { SidebarTrigger } from "./Sidebar"
import Button from "../Button"

interface BreadcrumbItem {
  label: string
  onClick?: () => void
}

interface BackButtonConfig {
  label: string
  action: () => void
}

interface TopBarProps {
  breadcrumbs?: BreadcrumbItem[]
  onSidebarToggle: () => void
  creditBalance?: number
  onBuyCredits?: () => void
  onViewAccount?: () => void
  showSearch?: boolean
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  actions?: React.ReactNode
  backButton?: BackButtonConfig | null
}

export function TopBar({
  breadcrumbs = [],
  onSidebarToggle,
  creditBalance,
  onBuyCredits,
  onViewAccount,
  showSearch = false,
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  actions,
  backButton
}: TopBarProps) {
  return (
    <header className="sticky top-0 z-20 h-16 bg-card border-b border-border px-4 lg:px-6">
      <div className="flex items-center justify-between h-full gap-4">
        {/* Left Section */}
        <div className="flex items-center gap-4 min-w-0">
          {/* Mobile Menu Trigger */}
          <SidebarTrigger onClick={onSidebarToggle} />
          
          {/* Back Button */}
          {backButton && (
            <button
              onClick={backButton.action}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{backButton.label}</span>
            </button>
          )}
          
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <nav className="hidden sm:flex items-center gap-1 text-sm">
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                  {item.onClick ? (
                    <button
                      onClick={item.onClick}
                      className="text-muted-foreground hover:text-foreground transition-colors truncate max-w-[150px]"
                    >
                      {item.label}
                    </button>
                  ) : (
                    <span className="text-foreground font-medium truncate max-w-[200px]">
                      {item.label}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )}
        </div>

        {/* Center Section - Search */}
        {showSearch && onSearchChange && (
          <div className="hidden md:flex flex-1 max-w-md mx-4">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full h-9 pl-9 pr-4 bg-muted/50 border border-input rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Right Section */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Custom Actions */}
          {actions}
          
          {/* Credit Balance */}
          {creditBalance !== undefined && (
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={onViewAccount}
                className="flex items-center gap-2 px-3 py-1.5 bg-accent rounded-lg hover:bg-accent/80 transition-colors"
              >
                <Coins className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">{creditBalance}</span>
                <span className="text-xs text-muted-foreground hidden lg:inline">credits</span>
              </button>
              {onBuyCredits && (
                <Button
                  onClick={onBuyCredits}
                  size="sm"
                  className="gap-1"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden lg:inline">Buy</span>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

// Page Header component for consistent page titles
interface PageHeaderProps {
  title: string
  description?: string
  actions?: React.ReactNode
  className?: string
}

export function PageHeader({ title, description, actions, className }: PageHeaderProps) {
  return (
    <div className={cn("flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6", className)}>
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{title}</h1>
        {description && (
          <p className="text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex items-center gap-2 shrink-0">
          {actions}
        </div>
      )}
    </div>
  )
}
